import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { BUILD_INFO_CONST } from '@lite-workspace/utils';
import { REDACTED_VALUE } from '@lite-workspace/secret-paths';
import type { ErrorEvent } from '@sentry/browser';
import { currentPageSecrets, redactPageSecrets, redactSecretPaths } from './page-secret-paths';

// В Sentry уходит только ошибка — сообщение и стек. Без трейсинга, replay, breadcrumbs
// (в них попадали бы клики, адреса запросов и консоль) и без данных пользователя.

export interface SentryErrorTrackingConfig {
  dsn: string | undefined;
  environment: string;
  // Один проект Sentry на весь фронт: приложение различается тегом.
  app: string;
}

let enabled = false;

const EMAIL_PATTERN = /[^\s@"'<>()]+@[^\s@"'<>()]+\.[a-z]{2,}/gi;
const MAX_EXTRA_DEPTH = 10;

// Без DSN — no-op: локально и в тестах поведение не меняется.
export function initSentryErrorTracking({ dsn, environment, app }: SentryErrorTrackingConfig): void {
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    // Короткий SHA сборки (set-build-info.js) — тот же, что SENTRY_RELEASE бэка и --release при загрузке карт.
    release: BUILD_INFO_CONST.commit,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    defaultIntegrations: false,
    integrations: [Sentry.globalHandlersIntegration(), Sentry.linkedErrorsIntegration(), Sentry.dedupeIntegration()],
    initialScope: { tags: { app } },
    beforeSend: scrubErrorEvent,
  });
  enabled = true;
}

export function reportErrorToSentry(error: unknown): void {
  if (!enabled) {
    return;
  }

  try {
    Sentry.captureException(error);
  } catch {
    // Трекинг не должен ломать обработку ошибки.
  }
}

/**
 * ErrorHandler для приложений без аналитики ошибок (admin, photobook): отдаёт ошибку в Sentry и
 * сохраняет стандартный вывод в консоль. Где уже стоит AnalyticsErrorHandler, он шлёт в Sentry сам.
 */
@Injectable({ providedIn: 'root' })
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    reportErrorToSentry((error as { rejection?: unknown } | null)?.rejection ?? error);
    console.error(error);
  }
}

// Адрес с секретом попадает не только в request: ещё в текст ошибки, кадры стека и extra.
export function scrubErrorEvent(event: ErrorEvent): ErrorEvent {
  const pageSecrets: string[] = currentPageSecrets();

  delete event.user;
  if (event.request) {
    event.request = { url: event.request.url ? scrubUrl(event.request.url, pageSecrets) : undefined };
  }

  for (const exception of event.exception?.values ?? []) {
    if (exception.value) {
      exception.value = scrubText(exception.value, pageSecrets);
    }

    // Без URL скрипта globalHandlers пишет в кадр весь location.href.
    for (const frame of exception.stacktrace?.frames ?? []) {
      if (frame.filename) {
        frame.filename = scrubUrl(frame.filename, pageSecrets);
      }

      if (frame.abs_path) {
        frame.abs_path = scrubUrl(frame.abs_path, pageSecrets);
      }
    }
  }

  if (event.message) {
    event.message = scrubText(event.message, pageSecrets);
  }

  // Не-Error (HttpErrorResponse Angular) SDK кладёт целиком в extra.__serialized__ — вместе с url и message запроса.
  if (event.extra) {
    scrubStringsIn(event.extra, pageSecrets, 0, new WeakSet());
  }

  return event;
}

function scrubText(text: string, pageSecrets: readonly string[]): string {
  return redactEmails(stripQueriesInText(redactPageSecrets(redactSecretPaths(text), pageSecrets)));
}

function scrubUrl(url: string, pageSecrets: readonly string[]): string {
  return stripQuery(redactPageSecrets(redactSecretPaths(url), pageSecrets));
}

function scrubStringsIn(value: object, pageSecrets: readonly string[], depth: number, seen: WeakSet<object>): void {
  if (depth >= MAX_EXTRA_DEPTH || seen.has(value)) {
    return;
  }

  seen.add(value);

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const entry: unknown = record[key];
    if (typeof entry === 'string') {
      record[key] = scrubText(entry, pageSecrets);
    } else if (entry !== null && typeof entry === 'object') {
      scrubStringsIn(entry, pageSecrets, depth + 1, seen);
    }
  }
}

function stripQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

function stripQueriesInText(text: string): string {
  // Двоеточие с пробелом после query — уже текст сообщения («…?a=1: 404»), а не часть адреса.
  return text.replace(/(https?:\/\/[^\s?#"']+)[?#][^\s"']*?(?=:?(?:[\s"']|$))/g, '$1');
}

function redactEmails(text: string): string {
  return text.replace(EMAIL_PATTERN, REDACTED_VALUE);
}
